import { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { ProjectElementType } from '@deltek/specification-client/core/Models/projectElement'
import type { AddProjectContentItemInputRequest } from '../types'
import type { ContentItemViewNodesType } from '../types/contentItemViewNode.type'
import { CreateContentItemInputBuilder } from './CreateContentItemInputBuilder'
import { ObjectBuilder } from './ObjectBuilder'
import {
    findMatchingFamilyProductType,
    getContentItemIds,
    isProductFamilyWithProductsPartType,
} from './builder.utils'

export interface DTVIdAndProjectNuggetId {
    documentTemplateViewId: string
    projectNuggetId: string
}

const NUGGET_TYPES = ['NUGGET', 'Nugget'] as const
const MAX_KEY_LENGTH = 150

/** Builds the input variables for adding a new Paragraph content item. */
export class ParagraphInputBuilder extends CreateContentItemInputBuilder {
    /**
     * Initializes the input builder for a Paragraph.
     * @param text - The initial text for the Paragraph.
     * @param siblingId - The sibling contentItemId used for context.
     * @param contentItemViewNodes - The list of all content item view nodes from store.
     */
    private familyAddedProductTypes?: ProjectElementNode[]
    private selectedProjectElementNodeId?: string
    private elementType?: ProjectElementType

    constructor(
        text: string,
        siblingId: string,
        contentItemViewNodes?: Partial<ContentItemViewNodesType>[],
        familyAddedProductTypes?: ProjectElementNode[],
        selectedProjectElementNodeId?: string,
        elementType?: ProjectElementType
    ) {
        super(text, siblingId, contentItemViewNodes)
        this.familyAddedProductTypes = familyAddedProductTypes
        this.selectedProjectElementNodeId = selectedProjectElementNodeId
        this.elementType = elementType
    }

    /**
     * Determines the appropriate documentTemplateViewId and projectNuggetId for the mutation based on family added product types.
     * For PRODUCTS part type, attempts to find a matching family added product type
     * and returns its DTVIdAndProjectNuggetId, otherwise returns the undefined.
     * @returns {documentTemplateViewId: string, projectNuggetId: string} | undefined.
     */
    protected resolveDTVIdAndProjectNuggetIdForProductType = ():
        | DTVIdAndProjectNuggetId
        | undefined => {
        if (
            !isProductFamilyWithProductsPartType(
                this.familyAddedProductTypes,
                this.contentItem,
                this.elementType
            )
        ) {
            return
        }

        const contentItemIds = getContentItemIds(this.contentItem)
        const matchingProductType = findMatchingFamilyProductType(
            this.familyAddedProductTypes,
            this.selectedProjectElementNodeId,
            contentItemIds
        )

        return matchingProductType
            ? {
                  documentTemplateViewId:
                      this.contentItem?.documentTemplateViewId || '',
                  projectNuggetId: matchingProductType?.elementId || '',
              }
            : undefined
    }

    /**
     * Checks if text contains a unique key (format: "key: description")
     * @param text - The text to check
     * @returns True if text has a key <= 150 chars before the colon
     */
    private hasUniqueKey(text: string): boolean {
        return text.includes(':') && 
            text.split(':')[0].trim().length <= MAX_KEY_LENGTH
    }

    /**
     * Determines if the content item is a newly added parent (not a nugget)
     * @returns True if item has dtvContentItemParentId and is not a nugget type
     */
    private isNewlyAddedNonNuggetParent(): boolean {
        const isNewlyAddedParent = this.contentItem?.dtvContentItemParentId != null
        const itemType = this.contentItem?.type || ''
        const isNugget = NUGGET_TYPES.some(nuggetType => nuggetType === itemType)
        return isNewlyAddedParent && !isNugget
    }

    /**
     * Gets the parent ID, prioritizing dtvContentItemParentId
     * @returns Parent ID or undefined
     */
    private getParentNode(): Partial<ContentItemViewNodesType> | undefined {
        const parentId =  this.contentItem?.dtvContentItemParentId || this.contentItem?.dtvParentId
        return this.contentItemViewNodes?.find(
            node => node.id === parentId || node.dtvItemId === parentId
        )
    }

    /**
     * Determines the content item owner ID based on parent node type
     * @param parentNode - The parent node
     * @returns Owner ID if applicable, undefined otherwise
     */
    private determineContentItemOwnerId(
        parentNode: Partial<ContentItemViewNodesType> | undefined
    ): string | undefined {
        const hasUniqueKey = this.hasUniqueKey(this.text || '')
        const parentId = parentNode?.id || parentNode?.dtvItemId

        if (hasUniqueKey || !parentId || parentId === this.contentItem?.id) {
            return undefined
        }

        const isParentArticle = parentNode?.textType === 'Article'

        if (parentNode?.type === 'ContentItem') {
            const ownerId = parentNode.propertyRefId && parentNode.propertyRefId !== '' 
                ? parentNode.propertyRefId 
                : parentNode.dtvItemId
            
            if (ownerId && ownerId !== parentId) {
                return ownerId
            }
        } else if (!isParentArticle) {
            return undefined
        }

        return undefined
    }

    /**
     * Builds the input variables for adding a new Article content item.
     * @returns The input object (Partial<AddProjectContentItemInputRequest['input']>).
     */
    buildInput(): Partial<AddProjectContentItemInputRequest['input']> {
        const builder = new ObjectBuilder<
            AddProjectContentItemInputRequest['input']
        >(this.buildBaseInput())

        // Set parent-related properties
        this.setParentProperties(builder)

        // Set product type properties if applicable
        const dtvIdAndProjectNuggetId = this.resolveDTVIdAndProjectNuggetIdForProductType()
        if (dtvIdAndProjectNuggetId) {
            builder
                .set('documentTemplateViewId', dtvIdAndProjectNuggetId.documentTemplateViewId)
                .set('projectNuggetId', dtvIdAndProjectNuggetId.projectNuggetId)
        }

        return builder.set('textType', this.contentItem?.textType || 'Paragraph').build()
    }

    /**
     * Determine the content item owner type based on contentItem's part type
     */
    private getContentItemOwnerType(): string {
        return this.contentItem?.partType?.toUpperCase() === 'PRODUCTS' ? 'ProductTypeProperty' : 'Nugget'
    }

    /**
     * Handles parent properties for newly added non-nugget items
     */
    private setNewlyAddedNonNuggetProperties(
        builder: ObjectBuilder<AddProjectContentItemInputRequest['input']>,
        parentNode: Partial<ContentItemViewNodesType> | undefined
    ): void {
        // For newly added items, use the dtvContentItemParentId directly if no parent node found
        const parentId = parentNode?.id || parentNode?.dtvItemId || this.contentItem?.dtvContentItemParentId
        const contentItemOwnerId = this.determineContentItemOwnerId(parentNode)

        if (contentItemOwnerId && contentItemOwnerId !== parentId) {
            builder.set('contentItemOwnerId', contentItemOwnerId)
        }

        builder
            .set('contentItemOwnerType','ProductTypeProperty')
            .set('contentItemParentId', parentId)
            .set('contentItemParentType', 'ContentItem')
    }

    /**
     * Checks if current content parent has content item children
     */
    private doesParentHaveContentItemChildren(
        parentNode: Partial<ContentItemViewNodesType> | undefined
    ): boolean {
        return this.contentItemViewNodes.some(
            x => x.dtvContentItemParentId === parentNode?.id && x.id !== this.contentItem?.id
        )
    }

    private shouldUseContentItemOwner(
        parentNode: Partial<ContentItemViewNodesType> | undefined
    ): boolean {
        // If true, store content in dtv content item
        const doesParentHaveContentItemChildren: boolean = this.doesParentHaveContentItemChildren(parentNode)

        return doesParentHaveContentItemChildren || (!this.contentItem?.dtvItemId &&
               !!this.contentItem?.dtvContentItemParentId &&
               !!parentNode?.dtvContentItemParentId &&
               !!parentNode?.dtvItemId &&
               !!parentNode?.dtvParentId)
    }

    /**
     * Resolves the parent ID for regular (non-newly-added) parents
     */
    private resolveParentIdForRegularParent(
        parentNode: Partial<ContentItemViewNodesType> | undefined
    ): string | undefined {
        if (this.shouldUseContentItemOwner(parentNode)) {
            return parentNode?.id
        }

        return parentNode?.dtvItemId ||
               this.contentItem?.dtvParentId ||
               this.contentItem?.dtvContentItemParentId
    }

    /**
     * Handles parent properties for regular (non-newly-added) items
     */
    private setRegularParentProperties(
        builder: ObjectBuilder<AddProjectContentItemInputRequest['input']>,
        parentNode: Partial<ContentItemViewNodesType> | undefined
    ): void {
        const parentId = this.resolveParentIdForRegularParent(parentNode)

        if (this.shouldUseContentItemOwner(parentNode)) {
            builder
                .set('contentItemOwnerId', parentNode!.dtvItemId)
                .set('contentItemParentType', 'ContentItem')
        } else {
            builder.set(
                'contentItemParentType',
                this.getParentType(parentNode?.dtvItemId || '')
            )
        }

        builder
            .set('contentItemOwnerType', this.getContentItemOwnerType())
            .set('contentItemParentId', parentId)
    }

    /**
     * Sets parent-related properties on the builder
     */
    private setParentProperties(
        builder: ObjectBuilder<AddProjectContentItemInputRequest['input']>
    ): void {
        const parentNode = this.getParentNode()

        if (this.isNewlyAddedNonNuggetParent()) {
            this.setNewlyAddedNonNuggetProperties(builder, parentNode)
        } else {
            this.setRegularParentProperties(builder, parentNode)
        }
    }

    /**
     * Builds the complete request object for adding a new Paragraph.
     * @returns The variables object (Partial<AddProjectContentItemInputRequest>).
     */
    build(): Partial<AddProjectContentItemInputRequest> {
        return new ObjectBuilder<AddProjectContentItemInputRequest>(
            this.buildBaseRequest()
        )
            .set('input', this.buildInput())
            .set(
                'rootArticleContentItemIds',
                this.getRootArticle(this.contentItem)
            )
            .build()
    }
}
